// Audio marginalia (VISION.md §3). Records a spoken note in the browser and
// transcribes it with the local whisper.cpp service (/lib/stt) — the audio
// never leaves the user's machines. Returns the transcript text.

import { remoteUrl } from "./remoteConfig";
import { db } from "./db";
import type { AudioNoteRecord } from "./db";

function audioUid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "a-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/** Keep the original recording (VISION.md §3) — local blob linked to the
 *  highlight, alongside the transcription. Never uploaded. */
export async function saveAudioNote(
  highlightId: string,
  bookId: string,
  blob: Blob,
): Promise<void> {
  await db.audioNotes.add({
    id: audioUid(),
    highlightId,
    bookId,
    blob,
    mimeType: blob.type || "audio/webm",
    createdAt: Date.now(),
  });
}

export async function listAudioNotes(highlightId: string): Promise<AudioNoteRecord[]> {
  return db.audioNotes.where("highlightId").equals(highlightId).sortBy("createdAt");
}

export async function deleteAudioNote(id: string): Promise<void> {
  await db.audioNotes.delete(id);
}

export function sttSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined"
  );
}

export async function transcribeAudio(blob: Blob): Promise<string> {
  const r = await fetch(remoteUrl("/lib/stt"), {
    method: "POST",
    headers: { "Content-Type": blob.type || "audio/webm" },
    body: blob,
  });
  if (!r.ok) throw new Error(`Transcription failed (${r.status})`);
  const d = (await r.json()) as { text?: string };
  return (d.text || "").trim();
}

/** A tiny recorder wrapper: start() opens the mic, stop() returns the recorded
 *  Blob. Kept minimal so UI components can drive their own record state. */
export class Recorder {
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: BlobPart[] = [];
  private stream: MediaStream | null = null;

  async start(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.chunks = [];
    this.mediaRecorder = new MediaRecorder(this.stream);
    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };
    this.mediaRecorder.start();
  }

  stop(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const mr = this.mediaRecorder;
      if (!mr) {
        reject(new Error("not recording"));
        return;
      }
      mr.onstop = () => {
        const blob = new Blob(this.chunks, { type: mr.mimeType || "audio/webm" });
        this.stream?.getTracks().forEach((t) => t.stop());
        this.stream = null;
        this.mediaRecorder = null;
        resolve(blob);
      };
      mr.stop();
    });
  }

  cancel(): void {
    try {
      this.mediaRecorder?.stop();
    } catch {
      /* ignore */
    }
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.mediaRecorder = null;
    this.chunks = [];
  }
}
